import { DatePipe, DecimalPipe, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseCRUDComponent } from 'app/core-ui/abstract/basegrid/baseCRUD.component';
import { ConfigService } from 'app/core-ui/abstract/services/config.service';
import { EventsEmitterService } from 'app/core-ui/abstract/services/emitter.service';
import { LogService } from 'app/core-ui/abstract/services/log.service';
import { VisualsService } from 'app/core-ui/abstract/services/visuals.service';
import { maxValueValidator, minValueValidator } from 'app/core-ui/dynamic-form/numericValueValidator.component';
import { Subject, forkJoin, takeUntil, throwError } from 'rxjs';
import { AuthFlowSetupService } from './authFlowSetup.service';

@Component({
  selector: 'authFlowSetupEdit-grid',
  templateUrl: './authFlowSetupEdit.component.html',
  styleUrls: ['./authFlowSetupEdit.component.css'],
  providers: [AuthFlowSetupService, DecimalPipe, DatePipe],
})
export class AuthFlowSetupEditComponent extends BaseCRUDComponent<AuthFlowSetupService> implements OnInit, OnDestroy {
  public formErrors = {};
  public submitError = null;

  public lookups: any[] = [];

  public minAuthCount = 0;

  customCurrencyConfig = {};

  fourEyes: boolean = false;
  fourEyesRequestId: string = '';
  foureyesComparisonId: number;
  differences: any;
  tagName: string;
  isCollapsed: boolean = true;
  isUpdating = false;
  verifierList: any[] = [];
  approverList: any[] = [];
  leftGroupsMapped: any[] = [];
  rightGroupsMapped: any[] = [];
  leftSequenceMapped: any[] = [];
  rightSequenceMapped: any[] = [];
  oldForm;

  protected disableAddCurrBtn = false;
  protected currCds: Set<String>;
  protected oldCds: Set<String>;
  protected availableCurrCds: any[];
  protected viewUpdated: boolean;
  private destroyed$ = new Subject<void>();
  private static readonly MIN_AMOUNT = 0;
  private static readonly MAX_AMOUNT = 999_999_999;

  constructor(
    private service: AuthFlowSetupService,
    private location: Location,
    public eventsEmitterService: EventsEmitterService,
    fb: UntypedFormBuilder,
    logger: LogService,
    visualsService: VisualsService,
    route: ActivatedRoute,
    router: Router,
    public config: ConfigService,
  ) {
    super(logger, visualsService, route, fb, router, 'authFlowSetupEdit');

    this.fillView();
    this.eventsEmitterService.languageChange.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (langId) => {
        this.loadVisuals();
        this.fillView();
      },
    });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  ngOnInit() {
    this.loadVisuals();
    this.urlParams = {};

    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (params) => {
        if (params['fourEyes']) {
          const cmpId = params['cmpId'];
          if (cmpId) {
            forkJoin({
              verifiers: this.service.getAsyncCompanyVerifiers(cmpId),
              approvers: this.service.getAsyncCompanyApprovers(cmpId),
            })
              .pipe(takeUntil(this.destroyed$))
              .subscribe(({ verifiers, approvers }) => {
                this.verifierList = verifiers;
                this.approverList = approvers;

                this.fourEyes = true;
                this.fourEyesRequestId = params['requestId'];
                this.foureyesComparisonId = params['foureyesComparisonId'];
                this.isUpdating = true;

                this.getFourEyesRequest();
              });
          }
        } else {
          this.urlParams = this.cleanParams(params);

          this.logger.warn(params);
          this.fillView();

          if (this.loadBean(this.urlParams) != null) {
            this.loading = true;
          }
        }
      },
      error: (error) => {
        return throwError(() => new Error(error));
      },
    });
  }

  private cleanParams(params: any): any {
    const { actionCd, cmpId } = params;
    return { actionCd, cmpId };
  }

  private getFourEyesRequest() {
    this.service
      .get4eyesRequest(this.fourEyesRequestId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data) => {
          const oldRec = JSON.parse(data.data.oldJsonRec);
          this.bean = JSON.parse(data.data.newJsonRec);
          this.differences = data.data.differences;
          this.loading = false;
          this.fourEyes = true;
          if (!this.differences.hasOwnProperty('flowPolicy')) {
            this.differences.flowPolicy = {
              leftValue: oldRec.flowPolicy,
              rightValue: this.bean.flowPolicy,
            };
          }
          if (this.differences.flowPolicy?.leftValue == 'TIERING') {
            this.oldForm = this.buildForm(oldRec);
            this.oldCds = new Set([
              'DEFAULT',
              ...this.differences.policyConfig.leftValue.tiers
                .map((tier) => tier.currCd)
                .filter((currCd) => currCd != 'DEFAULT' && currCd != null),
            ]);
          }
          if (this.differences.flowPolicy?.rightValue == 'TIERING') {
            this.currCds = new Set([
              'DEFAULT',
              ...this.differences.policyConfig.rightValue.tiers
                .map((tier) => tier.currCd)
                .filter((currCd) => currCd != 'DEFAULT' && currCd != null),
            ]);
            this.viewUpdated = true;
          }

          this.leftGroupsMapped = this.mapMembers(this.differences.policyConfig.leftValue.groups, this.approverList);

          this.rightGroupsMapped = this.mapMembers(this.differences.policyConfig.rightValue.groups, this.approverList);

          this.leftSequenceMapped = this.mapSequenceWithNames(
            this.differences.policyConfig.leftValue.sequence,
            this.verifierList,
            this.approverList,
          );

          this.rightSequenceMapped = this.mapSequenceWithNames(
            this.differences.policyConfig.rightValue.sequence,
            this.verifierList,
            this.approverList,
          );

          this.editForm = this.buildForm(this.bean);
        },
        error: (error) => {
          this.logger.info(error);
          this.loading = false;
        },
      });
  }

  public getGenericVisuals() {
    this.genericVisuals = this.visualsService.getFromStorageAsJSON('visualsArray')['generic'];
  }

  public fillView() {
    this.lookups['actionList'] = this.visualsService.getLkpFromStorageAsJSON('LKP_SEC_TESLA_FLOW_S_ACTION_CD', true);
    this.lookups['policyList'] = this.visualsService.getLkpFromStorageAsJSON('LKP_SEC_TESLA_FLOW_S_FLOW_POLICY', true);
    this.lookups['verifierList'] = this.service.getAsyncCompanyVerifiers(this.urlParams['cmpId']);
    this.lookups['approverList'] = this.service.getAsyncCompanyApprovers(this.urlParams['cmpId']);
    this.lookups['logicalConditions'] = this.visualsService.getLkpFromStorageAsJSON(
      'LKP_SEC_TESLA_FLOW_LOGIC_COND',
      true,
    );
  }

  public loadBean(map: any) {
    this.loading = true;
    this.setCustomCurrencyConfig();
    this.service
      .loadBean(this.urlParams['actionCd'], this.urlParams['cmpId'])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data) => {
          this.logger.log(data);
          this.bean = data;
          this.getValidationMessages();
          if (!this.fourEyes) {
            this.editForm = this.buildForm(this.bean);
          }
          this.loading = false;
        },
        error: (error) => {
          this.logger.log(error);
          this.router.navigateByUrl('menu/7213');
        },
      });
  }

  public save(bean: any) {
    this.submiting = true;

    let formValues = this.editForm.value;
    formValues['cmpId'] = this.urlParams['cmpId'];
    bean = { ...formValues, langId: localStorage.getItem('langId') };

    this.service
      .save(bean)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            LogService.emitAlert({
              type: 'success',
              body: response.message,
            });
            this.router.navigateByUrl('menu/7213');
          } else {
            LogService.emitAlert({
              type: 'error',
              body: response.message,
            });
          }
          this.submiting = false;
        },
        error: (error) => {
          this.submiting = false;
          return throwError(() => new Error(error));
        },
      });
  }

  public backToGrid() {
    if (this.fourEyes) {
      if (window.history.length > 1) {
        this.location.back();
        this.fourEyes = false;
      } else {
        this.router.navigateByUrl('menu/home');
      }
    } else {
      this.router.navigateByUrl('menu/7213');
    }
  }

  public buildForm(bean = this.bean) {
    switch (bean.flowPolicy) {
      case 'SEQUENTIAL':
        this.minAuthCount = 2;
        break;
      default:
        this.minAuthCount = 0;
        break;
    }
    let form = this.fb.group({
      // flow data
      actionCd: [bean.actionCd, []],
      cmpId: [bean.cmpId, []],
      authCount: [bean.authCount, [Validators.required, maxValueValidator(10)]],
      flowPolicy: [bean.flowPolicy, []],
      username: [bean.username, []],
      timestamp: [bean.timestamp, []],

      // policy configuration
      policyConfig: this.initPolicyConfig(bean.policyConfig),
    });

    // init form errors
    let kcontrols: any = Object.keys(form.controls);
    let controls: any = Object.assign({}, form.controls);

    kcontrols.forEach((element) => {
      if (controls[element] instanceof UntypedFormArray) {
        let formArray = [];
        controls[element].controls.forEach((ctrl) => {
          let arrkeys: any = Object.keys((<FormGroup>ctrl).controls);
          let innerCtrls = {};
          arrkeys.forEach((k) => {
            innerCtrls[k] = '';
          });
          formArray.push(innerCtrls);
        });

        controls[element.toString()] = formArray;
      } else {
        controls[element] = '';
      }
    });
    this.formErrors = controls;

    this.currCds = new Set([
      'DEFAULT',
      ...bean.policyConfig.tiers.map((tier) => tier.currCd).filter((currCd) => currCd != 'DEFAULT' && currCd != null),
    ]);
    this.refreshAvailableCurrencies();
    form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (data) => this.onValueChanged(data),
    });
    return form;
  }

  initPolicyConfig(policyConfig = this.bean.policyConfig) {
    let fbgroup = this.fb.group({
      table: [policyConfig?.table, []],
      sequence: this.initSequence(policyConfig),
      tiers: this.initTiers(policyConfig),
      groups: this.initGroups(),
      'basedOn': [
        this.bean.flowPolicy == 'TIERING'
          ? this.bean.policyConfig.basedOn == null
            ? 'sum'
            : this.bean.policyConfig.basedOn
          : null,
        [],
      ],
      'tierHierarchy': [this.bean.flowPolicy == 'TIERING' ? this.bean.policyConfig.tierHierarchy : 'sequential', []],
    });

    return fbgroup;
  }

  //sequence fns
  initSequence(policyConfig = this.bean.policyConfig) {
    let fbarray = this.fb.array([]);
    if (policyConfig.sequence) {
      policyConfig.sequence.forEach((element) => {
        fbarray.push(this.sequenceGroup(element));
      });
    }

    return fbarray;
  }

  sequenceGroup(element?: any) {
    if (!element) {
      this.bean.policyConfig.sequence.push({});
      element = this.bean.policyConfig.sequence[this.bean.policyConfig.sequence.length - 1];
    }

    return this.fb.group({
      rprId: [element.rprId ? String(element.rprId) : null, [Validators.required]],
    });
  }

  addSequence() {
    const control = <UntypedFormArray>(<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence'];
    control.push(this.sequenceGroup());
  }

  removeSequence(i: number) {
    const control = <UntypedFormArray>(<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence'];
    control.removeAt(i);
  }

  //tiers fns
  initTiers(policyConfig = this.bean.policyConfig) {
    let fbarray = this.fb.array([]);

    if (policyConfig.tiers) {
      policyConfig.tiers.forEach((element) => {
        fbarray.push(this.tiersGroup(element));
      });
    }

    return fbarray;
  }

  tiersGroup(element?: any) {
    return this.fb.group({
      minAmount: [element.minAmount, [minValueValidator(0), maxValueValidator(AuthFlowSetupEditComponent.MAX_AMOUNT)]],
      maxAmount: [element.maxAmount, [minValueValidator(0), maxValueValidator(AuthFlowSetupEditComponent.MAX_AMOUNT)]],
      workflow: this.initWorkflow(element.workflow),
      currCd: element.currCd ? element.currCd : 'DEFAULT',
    });
  }

  addTier(currCd: String) {
    const currTiers = this.spliceTiersForCurrCd(currCd);
    const lastCurrTier = currTiers[currTiers.length - 1];
    let newMinAmount = AuthFlowSetupEditComponent.MIN_AMOUNT;
    let newMaxAmount = AuthFlowSetupEditComponent.MAX_AMOUNT;
    if (lastCurrTier) {
      newMinAmount = +lastCurrTier.minAmount + 2;
      newMaxAmount = lastCurrTier.maxAmount > newMinAmount ? lastCurrTier.maxAmount : +newMinAmount + 1;
      lastCurrTier.maxAmount = +lastCurrTier.minAmount + 1;
    }
    currTiers.push({
      minAmount: newMinAmount,
      maxAmount: newMaxAmount,
      workflow: [{ groupId: 1, count: 1, rule: null }],
      currCd: currCd,
    });
    this.refreshTiers(currTiers);
  }

  removeTiers(event) {
    const currTiers = this.spliceTiersForCurrCd(event.currCd);
    if (event.index + 1 < currTiers.length) {
      currTiers[event.index + 1].minAmount = currTiers[event.index - 1].maxAmount + 1;
    }
    currTiers.splice(event.tierIndex, 1);
    this.refreshTiers(currTiers);
  }

  minAmountChanged(event: CurrTierEvent) {
    const currTiers = this.spliceTiersForCurrCd(event.currCd);
    const i = event.tierIndex;
    let minAmount = currTiers[i].minAmount;
    if (minAmount) {
      let length = currTiers.length;
      minAmount = minAmount > 0 ? minAmount : minAmount * -1;
      minAmount =
        minAmount <= AuthFlowSetupEditComponent.MAX_AMOUNT - 2 * (length - (i + 1)) - 1
          ? minAmount
          : AuthFlowSetupEditComponent.MAX_AMOUNT - 2 * (length - (i + 1)) - 1;
      currTiers[i].minAmount = minAmount;

      currTiers[i - 1].maxAmount = minAmount - 1;
      for (let j = i; j < length - 1; j++) {
        if (minAmount >= currTiers[j].maxAmount) {
          currTiers[j].maxAmount = +minAmount + 1;
          currTiers[j + 1].minAmount = +currTiers[j].maxAmount + 1;
          minAmount = currTiers[j + 1].minAmount;
        }
      }
      for (let k = i - 1; k > 0; k--) {
        if (currTiers[k].minAmount >= currTiers[k].maxAmount) {
          currTiers[k].minAmount = currTiers[k].maxAmount <= 0 ? 0 : currTiers[k].maxAmount - 1;
          currTiers[k - 1].maxAmount = currTiers[k].minAmount <= 0 ? 0 : currTiers[k].minAmount - 1;
        }
      }
      this.refreshTiers(currTiers);
    }
  }

  maxAmountChanged(event: CurrTierEvent) {
    const currTiers = this.spliceTiersForCurrCd(event.currCd);
    const i = event.tierIndex;
    let maxAmount = currTiers[i].maxAmount;
    if (maxAmount) {
      const length = currTiers.length;
      maxAmount = Math.abs(maxAmount);
      maxAmount =
        maxAmount <= AuthFlowSetupEditComponent.MAX_AMOUNT - 2 * (length - (i + 1))
          ? maxAmount
          : AuthFlowSetupEditComponent.MAX_AMOUNT - 2 * (length - (i + 1));
      currTiers[i].maxAmount = maxAmount;
      if (i + 1 < length) {
        currTiers[i + 1].minAmount = +maxAmount + 1;
      }
      for (let j = i + 1; j < length - 1; j++) {
        if (currTiers[j].minAmount >= currTiers[j].maxAmount) {
          currTiers[j].maxAmount = +currTiers[j].minAmount + 1;
          currTiers[j + 1].minAmount = +currTiers[j].maxAmount + 1;
        }
      }
      for (let k = i; k > 0; k--) {
        if (maxAmount <= currTiers[k].minAmount) {
          currTiers[k].minAmount = maxAmount <= 0 ? 0 : maxAmount - 1;
          currTiers[k - 1].maxAmount = currTiers[k].minAmount <= 0 ? 0 : currTiers[k].minAmount - 1;
          maxAmount = currTiers[k - 1].maxAmount;
        }
      }
      this.refreshTiers(currTiers);
    }
  }

  public refreshTiers(currTiers: any[]) {
    const allTiers: FormArray<any> = <FormArray<any>>this.editForm.get('policyConfig').get('tiers');
    currTiers.forEach((element, index) => {
      allTiers.push(this.tiersGroup(element));
    });
    this.viewUpdated = true;
  }

  //workflow fns
  initWorkflow(workflow: any) {
    let fbarray = this.fb.array([]);

    if (workflow) {
      workflow.forEach((element) => {
        fbarray.push(this.workflowGroup(element));
      });
    }

    return fbarray;
  }

  workflowGroup(element?: any) {
    return this.fb.group({
      groupId: [element.groupId, []],
      count: [element.count, []],
      rule: [element.rule, []],
    });
  }

  //groups fns
  initGroups(policyConfig = this.bean.policyConfig) {
    let fbarray = this.fb.array([]);

    if (policyConfig.groups) {
      policyConfig.groups.forEach((element) => {
        fbarray.push(this.groupsGroup(element));
      });
    }
    return fbarray;
  }

  groupsGroup(element?: any) {
    if (!element) {
      this.bean.policyConfig.groups.push({});
      element = this.bean.policyConfig.groups[this.bean.policyConfig.groups.length - 1];
    }

    return this.fb.group({
      groupId: [element.groupId, []],
      groupVisual: [element.groupVisual, []],
      members: [element.members ? element.members.map((x) => String(x)) : [], []],
    });
  }

  addGroups() {
    const control = <UntypedFormArray>(<UntypedFormGroup>this.editForm.get('policyConfig')).controls['groups'];
    control.push(
      this.groupsGroup({
        groupId: control.length + 1,
        groupVisual: null,
        members: [],
      }),
    );
    this.calcGroupItems();
  }

  removeGroups(i: number) {
    const control = <UntypedFormArray>(<UntypedFormGroup>this.editForm.get('policyConfig')).controls['groups'];
    control.removeAt(i);
    this.calcGroupItems();
  }

  calcGroupItems() {
    this.editForm.controls['policyConfig']['controls']['groups'].controls = [
      ...this.editForm.controls['policyConfig']['controls']['groups'].controls,
    ];
  }

  public getValidationMessages() {
    // Use "this.genericVisuals.genericerror" for a generic message
    this.validationMessages = {
      actionCd: { pattern: this.genericVisuals.interror },
      authCount: {
        required: this.genericVisuals.required,
        maxValue: 'Maximun allowed value exceeded',
      },
      flowPolicy: { pattern: this.genericVisuals.interror },
      username: { pattern: this.genericVisuals.interror },
      timestamp: { pattern: this.genericVisuals.interror },

      rprId: { pattern: this.genericVisuals.required.text },

      minAmount: {},
      maxAmount: {},
      workflow: { pattern: this.genericVisuals.interror },

      count: {},

      groupId: { pattern: this.genericVisuals.interror },
      groupVisual: { pattern: this.genericVisuals.interror },
      members: { pattern: this.genericVisuals.interror },
    };
  }

  onValueChanged(data?: any) {
    if (!this.editForm) {
      return;
    }
    const form = this.editForm;
    let controls: any = Object.assign({}, this.editForm.controls);

    for (const field in this.editForm.controls) {
      if (controls[field] instanceof UntypedFormArray) {
        controls[field].controls.forEach((ctrl, i) => {
          let arrkeys: any = Object.keys((<FormGroup>ctrl).controls);
          arrkeys.forEach((k) => {
            let control = (<FormGroup>ctrl).controls[k];
            // clear previous error message (if any)
            this.formErrors[field][i][k] = '';
            if (control && control.dirty && !control.valid) {
              const messages = this.validationMessages[k];
              for (const key in control.errors) {
                this.formErrors[field][i][k] += messages[key] + ' ';
              }
            }
          });
        });
      } else {
        // clear previous error message (if any)
        this.formErrors[field] = '';
        const control = form.get(field);
        if (control && control.dirty && !control.valid) {
          const messages = this.validationMessages[field];
          for (const key in control.errors) {
            this.formErrors[field] += messages[key] + ' ';
          }
        }
      }
    }
  }

  /**
   * ngx-select (module Output)
   * Option Selected
   */
  setValueSelected(name, value) {
    if (name == 'flowPolicy' && !!value) {
      if (value['id'] == 'BASIC') {
        this.minAuthCount = 0;
        this.editForm.get('authCount').setValue(1);
      } else if (value['id'] == 'SEQUENTIAL') {
        this.minAuthCount = 2;
        this.editForm.get('authCount').setValue(2);
        this.authPolicyCountUpdate();
      } else if (value['id'] == 'TIERING') {
        this.minAuthCount = 0;
        this.editForm.get('authCount').setValue(0);
      }
    }
  }

  authPolicyCountUpdate() {
    let authCnt = this.editForm.controls['authCount'].value;
    let oldCnt = (<UntypedFormArray>(<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence']).length;
    let diff: number = Number(authCnt) - oldCnt;
    let abcDiff = Math.abs(diff);
    let signDiff = Math.sign(diff);

    for (let index = 0; index < abcDiff; index++) {
      if (signDiff > 0) {
        this.addSequence();
      } else if (signDiff < 0) {
        let removeAt = (<UntypedFormArray>(<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence'])
          .length;
        this.removeSequence(removeAt - 1);
      }
    }
  }

  nextButtonDisabled() {
    let nextButtonDisabled = false;
    if (this.editForm.value['flowPolicy'] == 'SEQUENTIAL') {
      this.editForm.controls['policyConfig']['controls']['sequence'].controls.forEach((c) => {
        nextButtonDisabled = !c.value.rprId;
      });
    }
    if (this.editForm.value['flowPolicy'] == 'TIERING') {
      this.editForm.controls['policyConfig']['controls']['groups'].controls.forEach((group) => {
        nextButtonDisabled = !group.value.members || group.value.members.length == 0 || !group.value.groupVisual;
      });
      this.editForm.controls['policyConfig']['controls']['tiers'].controls.forEach((tiers) => {
        if (nextButtonDisabled) {
          return true;
        }
        nextButtonDisabled =
          tiers.value.minAmount == null || !tiers.value.maxAmount || tiers.value.workflow.length == 0;
        tiers.controls['workflow'].controls.forEach((wf) => {
          if (nextButtonDisabled) {
            return true;
          }
          nextButtonDisabled = !wf.controls.count.value || !wf.controls.groupId.value;
        });
      });
    }
    return nextButtonDisabled;
  }

  minAuthCountExceeded() {
    if (this.editForm.value['flowPolicy'] == 'SEQUENTIAL') {
      return this.editForm['controls']['authCount'].value == 0 || this.editForm['controls']['authCount'].value == 1;
    }
    return false;
  }

  getSelectedActionVisual() {
    let selectedAction = this.lookups['actionList'].filter((a) => a.id == this.bean.actionCd)[0];
    let label = selectedAction ? selectedAction.text : this.bean.actionCd;
    return label;
  }

  public setCustomCurrencyConfig() {
    this.customCurrencyConfig = { precision: 0 };
    if (this.config.getLocale() == 'el-GR') {
      Object.assign(this.customCurrencyConfig, {
        decimal: ',',
        thousands: '.',
      });
    } else if (this.config.getLocale() == 'ar-AE') {
      Object.assign(this.customCurrencyConfig, {
        decimal: '.',
        thousands: ',',
      });
    } else if (this.config.getLocale() == 'en-GB') {
      Object.assign(this.customCurrencyConfig, {
        decimal: '.',
        thousands: ',',
      });
    } else if (this.config.getLocale() == 'de-DE') {
      Object.assign(this.customCurrencyConfig, {
        decimal: ',',
        thousands: '.',
      });
    }
  }

  mapMembers(groups: any[], approverList: any[]): any[] {
    return groups.map((group) => ({
      ...group,
      members: (group.members || []).map((id: string) => ({
        id,
        name: approverList.find((u) => u.id == id)?.text || `Unknown (${id})`,
      })),
    }));
  }

  mapSequenceWithNames(sequence: any[], verifierList: any[], approverList: any[]) {
    return sequence.map((item, index) => {
      const isLast = index === sequence.length - 1;
      const listToUse = isLast ? approverList : verifierList;
      const user = listToUse.find((u) => String(u.id) == String(item.rprId));
      return {
        rprId: item.rprId,
        name: user ? user.text : `Unknown (${item.rprId})`,
      };
    });
  }

  hasRecFileAction(actions: any[]): boolean {
    return (
      Array.isArray(actions) && actions.some((a) => typeof a.actionCd === 'string' && a.actionCd.includes('REC_FILE'))
    );
  }

  getGroupVisualLeftValue(groupId: string): string {
    const group = this.differences?.policyConfig?.leftValue?.groups?.find((g) => g.groupId === groupId);
    return group?.groupVisual || groupId;
  }

  getGroupVisualRightValue(groupId: string): string {
    const group = this.differences?.policyConfig?.rightValue?.groups?.find((g) => g.groupId === groupId);
    return group?.groupVisual || groupId;
  }

  addTiersComponent(currCd?: String) {
    if (currCd == null) {
      currCd = this.availableCurrCds[0].text;
    }
    const control = <FormArray>(<FormGroup>this.editForm.get('policyConfig')).controls['tiers'];
    control.push(
      this.tiersGroup({
        minAmount: AuthFlowSetupEditComponent.MIN_AMOUNT,
        maxAmount: AuthFlowSetupEditComponent.MAX_AMOUNT,
        workflow: [{ groupId: 1, count: 1, rule: null }],
        currCd: currCd,
      }),
    );
    this.currCds = new Set([...this.currCds, currCd]);
    this.refreshAvailableCurrencies();
  }

  removeTiersComponent(currCdToRemove: String) {
    this.spliceTiersForCurrCd(currCdToRemove);
    this.currCds.delete(currCdToRemove);
    this.currCds = new Set(this.currCds);
    this.refreshAvailableCurrencies();
  }

  changeSelectedCurrCd(event: CurrTierEvent) {
    this.removeTiersComponent(event.oldCurrCd);
    this.addTiersComponent(event.currCd);
  }

  refreshAvailableCurrencies() {
    this.availableCurrCds = JSON.parse(localStorage.getItem('currencies_' + localStorage.getItem('langId'))).filter(
      (curr) => !this.currCds.has(curr.text),
    );
    if (this.availableCurrCds.length > 0) {
      this.disableAddCurrBtn = false;
    } else {
      this.disableAddCurrBtn = true;
    }
  }

  spliceTiersForCurrCd(currCd): any[] {
    const allTiers = <FormArray>this.editForm.get('policyConfig').get('tiers');
    const currTiers = [];
    allTiers.controls.forEach((control) => {
      if (control.value.currCd == currCd) {
        currTiers.push(control.value);
      }
    });
    let i = this.findIndexOfLastTier(currCd);
    while (i != -1) {
      allTiers.removeAt(i);
      i = this.findIndexOfLastTier(currCd);
    }
    return currTiers;
  }

  findIndexOfLastTier(currCd: String) {
    const allTiers = <FormArray>this.editForm.get('policyConfig').get('tiers');
    let i = -1;
    allTiers.controls.forEach((control, index) => {
      if (control.value.currCd == currCd) {
        i = index;
      }
    });
    return i;
  }

  findCurrTierIndex(currCd: String, index: Number): number {
    const allTiers = <FormArray>this.editForm.get('policyConfig').get('tiers');
    let indexForCurrCd = 0;
    for (let i = 0; i < allTiers.controls.length; i++) {
      if (allTiers.controls[i].value.currCd == currCd) {
        if (indexForCurrCd == index) {
          return i;
        }
        indexForCurrCd++;
      }
    }
    return -1;
  }

  tierChange(event: CurrTierEvent) {
    switch (event.event) {
      case CurrTierEventType.ADD_WORKFLOW:
        (<FormArray>event.tierItem.controls['workflow']).push(this.workflowGroup({ groupId: 1, count: 1, rule: null }));
        break;
      case CurrTierEventType.REMOVE_WORKFLOW:
        (<FormArray>(<FormGroup>event.tierItem).controls['workflow']).removeAt(event.workflowIndex);
        break;
      case CurrTierEventType.TIER_UPDATED:
        this.replaceTier(event);
        break;
      case CurrTierEventType.MIN_AMOUNT_CHANGED:
        this.minAmountChanged(event);
        break;
      case CurrTierEventType.MAX_AMOUNT_CHANGED:
        this.maxAmountChanged(event);
        break;
      case CurrTierEventType.CHANGE_CURR:
        this.changeSelectedCurrCd(event);
        break;
      case CurrTierEventType.REMOVE_TIER_COMPONENT:
        this.removeTiersComponent(event.currCd);
        break;
      case CurrTierEventType.ADD_TIER:
        this.addTier(event.currCd);
        break;
      case CurrTierEventType.REMOVE_TIER:
        this.removeTiers(event);
        break;
    }
    this.availableCurrCds = [...this.availableCurrCds];
  }

  replaceTier(event: CurrTierEvent) {
    const i = this.findCurrTierIndex(event.currCd, event.tierIndex);
    const allTiers = <FormArray>this.editForm.get('policyConfig').get('tiers');
    allTiers.value[i] = event.tierItem.value;
  }
}

export enum CurrTierEventType {
  ADD_WORKFLOW,
  REMOVE_WORKFLOW,
  TIER_UPDATED,
  MIN_AMOUNT_CHANGED,
  MAX_AMOUNT_CHANGED,
  CHANGE_CURR,
  REMOVE_TIER_COMPONENT,
  ADD_TIER,
  REMOVE_TIER,
}

export interface CurrTierEvent {
  currCd?: String;
  tierIndex?: number;
  tierItem?: FormGroup;
  event?: CurrTierEventType;
  workflowIndex?: number;
  oldCurrCd?: String;
}
